import { useMutation, useQuery, useQueryClient } from 'react-query';
import { httpRequest } from '../config/api';
import {
  GET_SHEET_CATEGORIES_FOR_PERIOD,
  GET_SHEET_CATEGORIES_FOR_SHEET
} from '../consts/queryKeys';

/**
 * Sheet ↔ data category mapping service.
 *
 * The backend OpenAPI spec exposes these schemas as
 * `SheetCategoryResponseSchema` / `SheetCategoryCreateRequestSchema` /
 * `SheetCategoryUpdateRequestSchema`. They are not yet in the local
 * generated types (the schema URL was empty when this file was added);
 * re-run `yarn generate-types` and switch consumers to the re-exports in
 * `src/types/workflow.ts` once it's wired.
 *
 * Mapping shape (server response):
 *   { id, sheet_id, period_id, workflow_id, data_category,
 *     is_custom, custom_category_id }
 *
 * `X-Active-Company-Id` and `workflow_id` headers are auto-injected by
 * `src/config/api.js`, so callers never set them explicitly.
 */
export const sheetCategoriesService = {
  getForPeriod: periodId => httpRequest.get(`api/periods/${periodId}/sheet-categories`),

  getForSheet: sheetId => httpRequest.get(`api/sheets/${sheetId}/sheet-categories`),

  create: data => httpRequest.post('api/sheet-categories', data),

  update: ({ id, data }) => httpRequest.put(`api/sheet-categories/${id}`, data),

  deleteOne: id => httpRequest.delete(`api/sheet-categories/${id}`),

  deleteAllForSheet: sheetId => httpRequest.delete(`api/sheets/${sheetId}/sheet-categories`),

  // `categoryState` is 'skip', 'lookup', or null to clear. Skip / Look-up replace the sheet's
  // categories on the server, and picking a category there clears the state again.
  setCategoryState: ({ sheetId, categoryState }) =>
    httpRequest.put(`api/sheets/${sheetId}/category-state`, { category_state: categoryState }),

  copyFromPreviousPeriod: data => httpRequest.post('api/automation/sheet-category-copy', data),

  mapWithAI: (periodId, sheetIds) =>
    httpRequest.post(
      `api/periods/${periodId}/sheet-categories/ai-map`,
      sheetIds?.length ? { sheet_ids: sheetIds } : undefined
    )
};

// A sheet's Skip / Look-up state comes with the period's file list (`sheet.category_state`). Writes
// that change it patch that list in place, so the page never reads a state the server just dropped
// while the list refetches.
export const patchSheetStatesInFiles = (queryClient, periodId, stateBySheetId) => {
  if (!periodId) return;
  queryClient.setQueriesData(['GET_PERIOD_FILES', periodId], files =>
    Array.isArray(files)
      ? files.map(file => ({
          ...file,
          sheets: (file?.sheets ?? []).map(sheet =>
            sheet && Object.prototype.hasOwnProperty.call(stateBySheetId, sheet.id)
              ? { ...sheet, category_state: stateBySheetId[sheet.id] }
              : sheet
          )
        }))
      : files
  );
};

const invalidateAll = (queryClient, periodId, sheetId) => {
  if (periodId) {
    queryClient.invalidateQueries([GET_SHEET_CATEGORIES_FOR_PERIOD, periodId]);
    // `category_mapping_progress_status` feeds the stepper's Mapping label — otherwise stale.
    queryClient.invalidateQueries(['GET_PERIOD_BY_ID']);
  }
  if (sheetId) {
    queryClient.invalidateQueries([GET_SHEET_CATEGORIES_FOR_SHEET, sheetId]);
  }
};

export const useGetSheetCategoriesForPeriod = ({ periodId, queryParams = {} } = {}) => {
  return useQuery(
    [GET_SHEET_CATEGORIES_FOR_PERIOD, periodId],
    () => sheetCategoriesService.getForPeriod(periodId),
    {
      enabled: !!periodId,
      ...queryParams
    }
  );
};

export const useGetSheetCategoriesForSheet = ({ sheetId, queryParams = {} } = {}) => {
  return useQuery(
    [GET_SHEET_CATEGORIES_FOR_SHEET, sheetId],
    () => sheetCategoriesService.getForSheet(sheetId),
    {
      enabled: !!sheetId,
      ...queryParams
    }
  );
};

export const useCreateSheetCategory = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.create, {
    onSuccess: (_data, variables) => {
      // The server clears Skip / Look-up when a category is picked.
      if (variables?.sheet_id)
        patchSheetStatesInFiles(queryClient, periodId, { [variables.sheet_id]: null });
      invalidateAll(queryClient, periodId, variables?.sheet_id);
    },
    ...queryParams
  });
};

export const useUpdateSheetCategory = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.update, {
    onSuccess: data => invalidateAll(queryClient, periodId, data?.sheet_id),
    ...queryParams
  });
};

export const useDeleteSheetCategory = ({ periodId, sheetId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.deleteOne, {
    onSuccess: () => invalidateAll(queryClient, periodId, sheetId),
    ...queryParams
  });
};

// Copy the previous period's mapping onto this one, sheet by sheet, using the file pairing the
// period already has. Taken verbatim from `feat/mappings-copy`, which introduced it for the
// dashboard's "Add Mappings" shortcut — same endpoint, same payload, so the two collapse into one
// definition whenever the branches meet.
export const useCopySheetCategories = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.copyFromPreviousPeriod, {
    onSuccess: (_data, variables) => {
      const id = periodId || variables?.period_id;
      invalidateAll(queryClient, id);
      // The copy carries Skip / Look-up too, and those live on the file list.
      if (id) queryClient.invalidateQueries(['GET_PERIOD_FILES', id]);
    },
    ...queryParams
  });
};

export const useDeleteAllSheetCategoriesForSheet = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.deleteAllForSheet, {
    onSuccess: (_data, sheetId) => {
      patchSheetStatesInFiles(queryClient, periodId, { [sheetId]: null });
      invalidateAll(queryClient, periodId, sheetId);
    },
    ...queryParams
  });
};

// The API clears one sheet per call, so a whole period is one call per sheet behind one mutation.
export const useClearSheetCategories = ({ periodId, mutationOptions = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    sheetIds => Promise.all(sheetIds.map(id => sheetCategoriesService.deleteAllForSheet(id))),
    {
      // Clearing a sheet drops its Skip / Look-up as well.
      onSuccess: (_data, sheetIds) =>
        patchSheetStatesInFiles(
          queryClient,
          periodId,
          Object.fromEntries(sheetIds.map(id => [id, null]))
        ),
      // Settled, not success: a run that failed part-way has still deleted some sheets' rows.
      onSettled: (_data, error) => {
        invalidateAll(queryClient, periodId);
        if (error && periodId) queryClient.invalidateQueries(['GET_PERIOD_FILES', periodId]);
      },
      ...mutationOptions
    }
  );
};

// No ids: the LLM fills in the sheets that are still unmapped. With ids: it re-maps exactly those
// sheets, mapped or not. Response is `{ created, removed, skipped, considered_sheet_count }` —
// `created` / `removed` are SheetCategoryResponseSchema rows written and deleted, `skipped` carries
// the sheets it left alone and why (`unchanged` keeps the same categories, `no_match` keeps the old).
export const useAIMapSheetCategories = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    // The only mutation here that closes over the id rather than reading it off the mutate
    // variables, so it is the only one that can bake `undefined` into the URL. The queries say this
    // with `enabled`; a mutation has to say it itself.
    sheetIds =>
      periodId
        ? sheetCategoriesService.mapWithAI(periodId, sheetIds)
        : Promise.reject(new Error('periodId is required')),
    {
      onSuccess: () => {
        invalidateAll(queryClient, periodId);
        // A re-mapped Skip / Look-up sheet loses its state on the server.
        if (periodId) queryClient.invalidateQueries(['GET_PERIOD_FILES', periodId]);
      },
      ...queryParams
    }
  );
};

export const useSetSheetCategoryState = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.setCategoryState, {
    onSuccess: (data, variables) => {
      const sheetId = data?.id ?? variables?.sheetId;
      if (sheetId) {
        patchSheetStatesInFiles(queryClient, periodId, {
          [sheetId]: data?.category_state ?? variables?.categoryState ?? null
        });
      }
      // Skip / Look-up delete the sheet's category rows on the server.
      invalidateAll(queryClient, periodId, sheetId);
    },
    ...queryParams
  });
};
